import * as _ from 'lodash';
import * as moment from 'moment';
import {addDays, format, startOfWeek} from 'date-fns';

import {Discrepancy, PayFactor, SplitBreakage, SummaryAmounts, UkSummaryAmounts} from '../timecard.model';
import {DayOfWeek, Field} from '../constants';
import {FlatUkWorkShifts, FlatWorkShifts} from '../services/timecard-discrepancy.service';
import {IsValidUUID} from './uuid';
import '../lodash-mixins/deepOmit';
import {daysOfWeek} from '../../client/app/shared/constants';
import {PunchTypes} from '../timecard.enum';
import {
  Breakages,
  CrewDetail,
  DetailValues,
  GetCrewDetail,
  ShiftDate,
  ShiftDates,
  UkPunch,
  UsaPunch,
  UsaPunchValues,
  UsaShifts
} from '../timecard.shifts.model';
import {SummaryBasics, SummaryItems, SummaryPayDetail} from '../timecard.summary-items.model';
import {StandardDateFormatString, StandardDateFormatString as StandardDateFormatString1} from '../date-format.constants';
import {
  IUkSummaryPayDetail,
  ShiftUnit,
  UkBreakages,
  UkCrewWorkDetail,
  UkPayFactors,
  UkTimecard
} from '../timecard-models/uk.model';
import {ApiUkTimecard} from '../../server/external-api/models/timecard-api.uk.model';
import {MapUkBreakages} from '../../server/api/timecard/mappers/timecard-breakage.uk.mapper';

export const RemoveSummaryItems = (summaryItems: SummaryItems, summaryIds: string[]): void => {
  _.each(summaryIds, (summaryId: string) => {
    delete summaryItems.accountCodes[summaryId];
    delete summaryItems.basics[summaryId];
    delete summaryItems.dates[summaryId];
    delete summaryItems.jobDetails[summaryId];
    delete summaryItems.payDetails[summaryId];
  });
};

// USA Week Ending Date - Saturday
export const CalculateUsaWeekEndingDate = (shiftDate: string, dateFormat: string): string => {
  return moment.utc(shiftDate, dateFormat).day(DayOfWeek.Saturday).format(StandardDateFormatString);
};

/**
 * UK Week Ending Date - Sunday
 * For UK - weekEndingDay should be 7 for non-Sunday days. (if weekEndingDay is 0, it would give the previous week Sunday)
 * But if the shiftDate is a Sunday, then it should return the same date.
 */
export const CalculateUkWeekEndingDate = (shiftDate: string, dateFormat: string): string => {
  const weekEndingDay: DayOfWeek = moment.utc(shiftDate, dateFormat).day() === 0 ? DayOfWeek.Sunday : DayOfWeek.NextSunday;
  return moment.utc(shiftDate, dateFormat).day(weekEndingDay).format(StandardDateFormatString);
};

export const HasBreakageOverride = (breakages: Breakages|undefined): boolean => {
  if (_.isNil(breakages)) {
    return false;
  }
  return _
    .chain(breakages)
    .map(({payFactors = []}) => payFactors)
    .flatten()
    .filter((payFactor: PayFactor) => !_.isUndefined(payFactor.originalHours))
    .some(({originalHours, duration}: PayFactor) => originalHours !== duration.value)
    .value();
};
export const HasUkBreakageOverride = (breakages: UkBreakages|undefined): boolean => {
  if (_.isNil(breakages)) {
    return false;
  }
  return _
    .chain(breakages)
    .map(({payFactors = {}}) => _.values(payFactors)) // UK payFactors is converted to array (unlike US which uses an array directly)
    .flatten()
    .filter((payFactor: UkPayFactors[string]) => !_.isUndefined(payFactor.originalDuration))
    .some(({originalDuration, duration}: UkPayFactors[string]) => originalDuration !== duration.value)
    .value();
};
// Takes a Shifts and the target ShiftId to generate the SplitBreakage object for BreakageOverride
export const MapSplitBreakageForBreakageOverride = (shiftId: string, shifts: UsaShifts, summaryItems: SummaryItems): SplitBreakage => {
  const mpSummaryId: string = _
    .chain(summaryItems.basics)
    .mapValues((summaryBasic: SummaryBasics, summaryId: string) => {
      console.log(`summaryId: ${summaryId}`);
      return {...summaryBasic, summaryId};
    })
    .values()
    .find(({isHTGItem, isFlatItem, isShieldItem}) => isHTGItem && isFlatItem && !isShieldItem)
    .get('summaryId')
    .value();
  const mpPayCode: string = _.get(summaryItems, `payDetails['${mpSummaryId}'].payCode.value`, '');

  const {payPunches, workDetails, breakages} = shifts;
  if (_.isUndefined(payPunches) || _.isUndefined(breakages)) {
    throw new Error(`Shift Punches and Breakages is undefined`);
  }
  const {payFactors = [], totalAmount, MP1Amount, MP2Amount} = breakages[shiftId];
  const shiftReRate = _.get(shifts, `shiftRates['${shiftId}'].rerateAmount.value`, 0);
  const breakageRate: number = _.chain(payFactors).map(({rate = 0}) => rate).uniq().head().value();
  return {
    shiftId,
    punches: payPunches[shiftId],
    workDetails: GetCrewDetail(workDetails[shiftId]),
    breakages: _.map(payFactors, ({payFactor, payCode = '', duration: {value: duration = 0}, rate = 0}: PayFactor) => ({
      payFactor,
      duration,
      rate,
      amount: totalAmount,
      payCode,
      isFlatAmount: false
    })),
    mealPenalties: _.filter([
      {amount: MP1Amount, occurrenceTime: '', payCode: mpPayCode},
      {amount: MP2Amount, occurrenceTime: '', payCode: mpPayCode}
    ], ({amount}) => amount > 0),
    totalAmount: totalAmount,
    totalMealPenalties: MP1Amount + MP2Amount,
    nonMPTotalAmount: totalAmount - (MP1Amount + MP2Amount),
    shiftRate: shiftReRate === 0 ? breakageRate : shiftReRate
  };
};

export const HasPayPunchFields = (punch: UsaPunch|UsaPunchValues|UkPunch): boolean => _.some(PunchTypes, (punchHeader: string) => _.has(punch, punchHeader));
export const HasPunchValues = (punch: UsaPunch): boolean => _.some(PunchTypes, (punchHeader: string) => {
  const p = punch[punchHeader];
  return !_.isUndefined(p)
    ? _.isNumber(p.value)
    : false;
});
export const IsNotWorked = (workDetail: CrewDetail): boolean => _.isEmpty(workDetail.dayType1.value) || workDetail.dayType1.value === 'NOT_WORKED';

export const IsShiftTimesEmpty = (
  payPunch: UsaPunch,
  workDetail: CrewDetail
): boolean => {
  return _
    .chain(payPunch)
    .omit('shiftId')
    .isEmpty()
    .value() && _.isEmpty(workDetail?.dayType1?.value) && _.isEmpty(workDetail?.dayType2?.value) && _.isEmpty(workDetail?.payType?.value);
};
export const IsUkShiftTimesEmpty = (
  payPunch: UkPunch,
  workDetail: UkCrewWorkDetail
): boolean => {
  return _
    .chain(payPunch)
    .omit('shiftId')
    .isEmpty()
    .value() && _.isEmpty(workDetail?.dayType1?.value) && _.isEmpty(workDetail?.dayType2?.value) && _.isEmpty(workDetail?.payType?.value);
};
/**
 * Property "shiftId" required only for paytime punches, Omits last punch if present
 * Reassigns `${punchType}.value` to respective punchType for easier comparison
 */
export const MapPunchToPunchValues = (punch: UsaPunch): UsaPunchValues => {
  const {shiftId, ...punches} = _.omit(punch, Field.Last);
  return _.reduce(_.keys(punches), (punchValue, field) => {
    const value = _.get(punches, `${field}.value`);
    if (!_.isUndefined(value)) {
    punchValue[field] = _.get(punches, `${field}.value`);
    }
    return punchValue;
  }, {});
};

export const MapWorkDetailsToDetailsValue = (detail: CrewDetail): DetailValues => {
  const details = _.pick(detail, [Field.DayType, Field.PayType]);
  return _.reduce(_.keys(details), (detailValue, field) => {
    detailValue[field] = _.get(details, `${field}.value`);
    return detailValue;
  }, {});
};
export const GetFlatWorkShiftsDiscrepancies = (
  primaryShifts: FlatWorkShifts,
  secondaryShifts: FlatWorkShifts,
  getDiscrepanciesFunc: IGetDiscrepancies
): Discrepancy[] => _
  .chain(primaryShifts)
  .map(({punches: primaryPunches, workDate, isSplitShift: primaryIsSplitShift, dayType1: primaryDayType1 = ''}, key) => {
    const secondaryPunches: UsaPunchValues = _.get(secondaryShifts[key], 'punches', {shiftId: primaryPunches.shiftId});
    const secondaryDayType1: string = _.get(secondaryShifts[key], 'dayType1', '');
    const isBothSplitShifts = primaryIsSplitShift && _.get(secondaryShifts[key], 'isSplitShift', false);
    return getDiscrepanciesFunc(primaryPunches, secondaryPunches , primaryPunches.shiftId, primaryDayType1, secondaryDayType1, isBothSplitShifts);
  })
  .filter(Boolean)
  .flatten()
  .value();

export const GetFlatUkWorkShiftsDiscrepancies = (
  primaryShifts: FlatUkWorkShifts,
  secondaryShifts: FlatUkWorkShifts,
  getDiscrepanciesFunc: IGetUkDiscrepancies
): Discrepancy[] => _
  .chain(primaryShifts)
  .map(({punches: primaryPunches, isSplitShift: primaryIsSplitShift, dayType1: primaryDayType1 = '', units: primaryUnits}, key) => {
    const secondaryPunches: UsaPunchValues = _.get(secondaryShifts[key], 'punches', {shiftId: primaryPunches.shiftId});
    const secondaryDayType1: string = _.get(secondaryShifts[key], 'dayType1', '');
    const secondaryUnits = _.get(secondaryShifts[key], 'units');
    const isBothSplitShifts = primaryIsSplitShift && _.get(secondaryShifts[key], 'isSplitShift', false);
    return getDiscrepanciesFunc(primaryPunches, secondaryPunches , primaryPunches.shiftId, primaryDayType1, secondaryDayType1, primaryUnits, secondaryUnits, isBothSplitShifts);
  })
  .filter(Boolean)
  .flatten()
  .value();

export type IGetDiscrepancies = (primaryPunch: UsaPunchValues | null,
                                 secondaryPunch: UsaPunchValues,
                                 entityId: string,
                                 primaryDayType1: string,
                                 secondaryDayType1: string,
                                 isSplitShift: boolean) => Discrepancy[];
export const GetDiscrepancies: IGetDiscrepancies = (
  primaryPunch: UsaPunchValues | null,
  secondaryPunch: UsaPunchValues,
  entityId: string,
  primaryDayType1: string = null,
  secondaryDayType1: string = null,
  isSplitShift: boolean = false) => {
  if (!primaryPunch) {
    return null;
  }
  // STF-18450 - Ignore Wrap field discrepancy when shift is a Split Shift and both Primary
  //  and Secondary DayType is either SICK/VAC and Primary and Secondary are the same.
  if (
    isSplitShift &&
    (primaryDayType1 === 'SICK' || primaryDayType1 === 'VAC') &&
    (secondaryDayType1 === 'SICK' || secondaryDayType1 === 'VAC') &&
    primaryDayType1 === secondaryDayType1
  ) {
    return [];
  }
  // STF-18829 - Mobile to WTC - Update discrepancy logic for duration and not worked
  const workDetailDiscrepancies: Discrepancy[] = (primaryDayType1 !== secondaryDayType1) && !(_.isEmpty(primaryDayType1) && secondaryDayType1 === 'NOT_WORKED')
    ? [{entityId, workDetailsType: 'dayType1'}]
    : [];
  if ( (primaryDayType1 === 'SICK' || primaryDayType1 === 'VAC') &&
    (secondaryDayType1 === 'SICK' || secondaryDayType1 === 'VAC') && _hasWrapValueOnly(primaryPunch)) {
    return workDetailDiscrepancies;
  }
  // For payTimesToMobileTimes and dailyTimesToMobiles discrepancies, ndb1In & ndb2In shouldn't be included as mobileTimes doesn't have these.
  const {shiftId: primaryShiftId, ...primaryPunches} = _.omit(primaryPunch, [Field.Ndb1In, Field.Ndb2In]);
  const {shiftId: secondaryShiftId, ...secondaryPunches} = _.omit(secondaryPunch, [Field.Ndb1In, Field.Ndb2In]);
  // primary punches compared against secondary punches except last punches
  const primaryDiscrepancyPunches = _.filter(_.keys(primaryPunches),
    punchType => {
      const primaryPunchValue = _.get(primaryPunches, punchType);
      const secondaryPunchValue = _.get(secondaryPunches, punchType);
      return _.includes([PunchTypes.ndb1Out, PunchTypes.ndb2Out], punchType)
        ? _.isUndefined(primaryPunchValue) !== _.isUndefined(secondaryPunchValue)
        : primaryPunchValue !== secondaryPunchValue;
    });
  // secondary punches compared against primary punches since secondary punches could have punches which are not in primary punches
  // & since those are also treated as discrepancies except last punches
  const secondaryDiscrepancyPunches = _.filter(_.keys(secondaryPunches),
    punchType => {
      const primaryPunchValue = _.get(primaryPunches, punchType);
      const secondaryPunchValue = _.get(secondaryPunches, punchType);
      return _.includes([PunchTypes.ndb1Out, PunchTypes.ndb2Out], punchType)
        ?  _.isUndefined(primaryPunchValue) !== _.isUndefined(secondaryPunchValue)
        : !_.includes(primaryDiscrepancyPunches, punchType) && secondaryPunchValue !== primaryPunchValue;
    });
  const punchDiscrepancies: Discrepancy[] = _.map(_.concat(primaryDiscrepancyPunches, secondaryDiscrepancyPunches), punchType => ({entityId, punchType}));
  return  _.concat(punchDiscrepancies, workDetailDiscrepancies);
};

export type IGetUkDiscrepancies = (primaryPunch: UsaPunchValues | null,
                                   secondaryPunch: UsaPunchValues,
                                   entityId: string,
                                   primaryDayType1: string,
                                   secondaryDayType1: string,
                                   primaryUnits: ShiftUnit,
                                   secondaryUnits: ShiftUnit,
                                   isSplitShift: boolean) => Discrepancy[];
export const GetUkDiscrepancies: IGetUkDiscrepancies = (
  primaryPunch: UsaPunchValues | null,
  secondaryPunch: UsaPunchValues,
  entityId: string,
  primaryDayType1: string = null,
  secondaryDayType1: string = null,
  primaryUnit: ShiftUnit,
  secondaryUnit: ShiftUnit,
  isSplitShift: boolean = false) => {

  const punchAndWorkDetailDiscrepancies = GetDiscrepancies(primaryPunch, secondaryPunch, entityId, primaryDayType1, secondaryDayType1, isSplitShift);
  const {shiftId: primaryShiftId, ...primaryUnits} = _.omit(primaryUnit, ['htgCalcUnitShift']);
  const {shiftId: secondaryShiftId, ...secondaryUnits} = _.omit(secondaryUnit, ['htgCalcUnitShift']);
  const primaryDiscrepancyUnits = _.filter(_.keys(primaryUnits),
    unitType => {
      const primaryUnitValue = _.get(primaryUnits, `${unitType}.value`);
      const secondaryUnitValue = _.get(secondaryUnits, `${unitType}.value`);
      return primaryUnitValue !== secondaryUnitValue;
    });
  const secondaryDiscrepancyUnits = _.filter(_.keys(secondaryUnits),
    unitType => {
      const primaryUnitValue = _.get(primaryUnits, `${unitType}.value`);
      const secondaryUnitValue = _.get(secondaryUnits, `${unitType}.value`);
      return !_.includes(primaryDiscrepancyUnits, unitType) && secondaryUnitValue !== primaryUnitValue;
    });
  const unitsDiscrepancies: Discrepancy[] = _.map(_.concat(primaryDiscrepancyUnits, secondaryDiscrepancyUnits), punchType => ({entityId, punchType}));
  return  _.concat(punchAndWorkDetailDiscrepancies, unitsDiscrepancies);
};

export const GetDailyTimeToPayTimeDiscrepancies: IGetDiscrepancies = (
  primaryPunch: UsaPunchValues | null,
  secondaryPunch: UsaPunchValues,
  entityId: string,
  primaryDayType1: string = null,
  secondaryDayType1: string = null,
  isSplitShift: boolean = false) => {
  if (!primaryPunch) {
    return null;
  }
  // STF-18450 - Ignore Wrap field discrepancy when shift is a Split Shift and both Primary
  //  and Secondary DayType is either SICK/VAC and Primary and Secondary are the same.
  if (
    isSplitShift &&
    (primaryDayType1 === 'SICK' || primaryDayType1 === 'VAC') &&
    (secondaryDayType1 === 'SICK' || secondaryDayType1 === 'VAC') &&
    primaryDayType1 === secondaryDayType1
  ) {
    return [];
  }
  const {shiftId: primaryShiftId, ...primaryPunches} = primaryPunch;
  const {shiftId: secondaryShiftId, ...secondaryPunches} = secondaryPunch;
  if (!IsValidUUID(secondaryShiftId)) {
    return [];
  }
  // primary punches compared against secondary punches except last punches
  const primaryDiscrepancyPunches = _.filter(_.keys(primaryPunches),
    punchType => _.get(primaryPunches, punchType) !== _.get(secondaryPunches, punchType));
  // secondary punches compared against primary punches since secondary punches could have punches which are not in primary punches
  // & since those are also treated as discrepancies except last punches
  const secondaryDiscrepancyPunches = _.filter(_.keys(secondaryPunches),
    punchType => !_.includes(primaryDiscrepancyPunches, punchType) && _.get(secondaryPunches, punchType) !== _.get(primaryPunches, punchType));
  const punchDiscrepancies: Discrepancy[] = _.map(_.concat(primaryDiscrepancyPunches, secondaryDiscrepancyPunches), punchType => ({entityId, punchType}));

  // STF-20212, No discrepancy should be shown between dayTypes when no paytimes, payTime dayType1=blank & dailyTime dayType1='NOT_WORKED'
  const workDetailDiscrepancies: Discrepancy[] = (
    _isPayPunchEmpty(primaryPunch) &&
    _isPayPunchEmpty(secondaryPunch) &&
    _.isEmpty(primaryDayType1) && secondaryDayType1 === 'NOT_WORKED') || primaryDayType1 === secondaryDayType1
    ? []
    : [{entityId, workDetailsType: 'dayType1'}];

  return  _.concat(punchDiscrepancies, workDetailDiscrepancies);
};

export const CalculateSummaryTotals = (summaryPayDetails: SummaryPayDetail[]): SummaryAmounts => {
  const totalWages = summaryPayDetails.filter((summaryPayDetail: SummaryPayDetail) => summaryPayDetail?.payCodeDetails?.isWages)
    .reduce(function (start: number, payDetail: SummaryPayDetail) {
      return start + payDetail?.amount?.value;  // return previous total plus current amount
    }, 0);
  const totalOtherPayments = summaryPayDetails.filter((summaryPayDetail: SummaryPayDetail) => summaryPayDetail?.payCodeDetails?.isOtherPayments)
    .reduce(function (start: number, payDetail: SummaryPayDetail) {
      return start + payDetail?.amount?.value;  // return previous total plus current amount
    }, 0);
  const totalDeduction = summaryPayDetails.filter(({payCodeDetails}: SummaryPayDetail) => payCodeDetails?.isDeduction && payCodeDetails?.payType1 === 'ADVANCE')
    .reduce(function (start: number, payDetail: SummaryPayDetail) { // return previous total plus current amount
      return start + (payDetail?.amount?.value * -1);  // Based on story STF-12610
    }, 0);
  const wagesAmount = _.inRange(_.round(totalWages, 4), -0.0099, 0) ? 0 : totalWages;
  const otherAmount = _.inRange(_.round(totalOtherPayments, 4), -0.0099, 0) ? 0 : totalOtherPayments;
  const deductionAmount = _.inRange(_.round(totalDeduction, 4), -0.0099, 0) ? 0 : totalDeduction;
  const total = totalWages + totalOtherPayments + totalDeduction;
  const totalAmount = _.inRange(_.round(total, 4), -0.0099, 0) ? 0 : total;

  return {wagesAmount, otherAmount, deductionAmount, totalAmount};
};

export const CalculateUkSummaryTotals = (summaryPayDetails: IUkSummaryPayDetail[]): UkSummaryAmounts => {
  const totalBasePayments = summaryPayDetails.filter(({payCodeDetails}: IUkSummaryPayDetail) => payCodeDetails?.isBasePay)
    .reduce(function (start: number, payDetail: IUkSummaryPayDetail) {
      return start + (payDetail?.amount?.value || 0);  // return previous total plus current amount
    }, 0);
  const totalOtherPayments = summaryPayDetails.filter(({payCodeDetails}: IUkSummaryPayDetail) => payCodeDetails?.isOtherPayments)
    .reduce(function (start: number, payDetail: IUkSummaryPayDetail) {
      return start + (payDetail?.amount?.value || 0);  // return previous total plus current amount
    }, 0);
  const totalAdditional = summaryPayDetails.filter(({payCodeDetails}: IUkSummaryPayDetail) => payCodeDetails?.isAdditionals)
    .reduce(function (start: number, payDetail: IUkSummaryPayDetail) {
      return start + (payDetail?.amount?.value || 0);
    }, 0);
  const totalAllowances = summaryPayDetails.filter(({payCodeDetails}: IUkSummaryPayDetail) => payCodeDetails?.isAllowance)
    .reduce(function (start: number, payDetail: IUkSummaryPayDetail) {
      return start + (payDetail?.amount?.value || 0);
    }, 0);
  const totalDeduction = summaryPayDetails.filter(({payCodeDetails}: IUkSummaryPayDetail) => payCodeDetails?.isDeduction)
    .reduce(function (start: number, payDetail: IUkSummaryPayDetail) { // return previous total plus current amount
      return start + ((payDetail?.amount?.value || 0) * -1);  // Based on story STF-12610
    }, 0);
  const totalHolidayPay = summaryPayDetails
    .reduce(function (start: number, payDetail: IUkSummaryPayDetail) {
      return start + (payDetail?.holidayPay || 0);  // Sum all holiday pay amounts
    }, 0);
  const baseAmount = _.inRange(_.round(totalBasePayments, 4), -0.0099, 0) ? 0 : totalBasePayments;
  const otherAmount = _.inRange(_.round(totalOtherPayments, 4), -0.0099, 0) ? 0 : totalOtherPayments;
  const additionalAmount = _.inRange(_.round(totalAdditional, 4), -0.0099, 0) ? 0 : totalAdditional;
  const allowanceAmount = _.inRange(_.round(totalAllowances, 4), -0.0099, 0) ? 0 : totalAllowances;
  const deductionAmount = _.inRange(_.round(totalDeduction, 4), -0.0099, 0) ? 0 : totalDeduction;
  const holidayAmount = _.inRange(_.round(totalHolidayPay, 4), -0.0099, 0) ? 0 : totalHolidayPay;
  const total = totalBasePayments + totalOtherPayments + totalDeduction + additionalAmount + allowanceAmount + holidayAmount;
  const totalAmount = _.inRange(_.round(total, 4), -0.0099, 0) ? 0 : total;

  // Need to figure out how ot calculate additionalAmount later. Check with Herbert or Tania.
  return {wagesAmount: 0, baseAmount, otherAmount, deductionAmount, totalAmount, additionalAmount, allowanceAmount, holidayAmount};
};

export const CalculateOrderedWeekDays = (weekEndingDate: string, weekStartsOn: DayOfWeek): Date[] => {
  const weekEndingMomentDate = moment.utc(weekEndingDate, StandardDateFormatString).toDate();
  // create an array of dates that starts on Sunday and Ends on Saturday.
  // Then we "shift" x number of days based on the PayrollWeekDisplay setting to create the OrderedWeekDays array
  const beginningOfWeek = startOfWeek(weekEndingMomentDate, {weekStartsOn});
  return _
    .chain(new Array(7))
    .map((i, idx) => {
      return addDays(beginningOfWeek, idx);
    })
    .value();
};

export const CalculateOrderedShifts = (weekEndingDate: string, shiftDates: ShiftDates, weekStartsOn: DayOfWeek): ShiftDate[] => {
  const orderedWeekDays = CalculateOrderedWeekDays(weekEndingDate, weekStartsOn);
  const orderedShifts = _
    .chain(shiftDates)
    .values()
    .orderBy(['date', 'splitShiftOrdinal'])
    .map(({shiftId, date, splitShiftOrdinal}) => ({shiftId, date, splitShiftOrdinal}))
    .value();
  const numberOfShiftsToBeRemoved = _.findIndex(orderedShifts, ({date}) => date === format(orderedWeekDays[0], StandardDateFormatString1));
  const splicedShifts = orderedShifts.splice(0, numberOfShiftsToBeRemoved);
  return _.concat(orderedShifts, splicedShifts);
};

/**
 * IsSplitShift is a utility method that will determine a given work date is a Split Shift (i.e. it has more than 1 shifts for the same work date)
 * Note that this does not check if the workDetails or punches are filled, it simply checks the number of shifts for a given work date.
 * work date must be in StandardDateFormat
 * */
export const IsSplitShift = (shiftDates: ShiftDates, workDate: string): boolean => {
  if (moment(workDate, StandardDateFormatString).format(StandardDateFormatString) !== workDate) {
    throw new Error('[IsSplitShift] workDate is not in the StandardDateFormatString');
  }
  return _
    .chain(shiftDates)
    .values()
    .filter(({date}) => workDate === date)
    .value()
    .length > 1;
};

/**
 * IsSplitShiftTemplate is a utility method that will determine a given work date is a Split Shift for template shifts
 * (i.e. it has more than 1 shifts for the same work date)
 * Note that this does not check if the workDetails or punches are filled, it simply checks the number of shifts for a given work date.
 * work date must be in string format and must have any of these values: [Sun, Mon, Tue, Wed, Thu, Fri, Sat]
 * */
export const IsSplitShiftTemplate = (shiftDates: ShiftDates, workDate: string): boolean => { // workDate must be of format Mon, Tue, Wed ....
  if (!_.includes(daysOfWeek, workDate)) {
    throw new Error('[IsSplitShiftTemplate] workDate is not in [Sun, Mon, Tue, Wed, Thu, Fri, Sat] string format');
  }
  return _
    .chain(shiftDates)
    .values()
    .filter(({date}) => workDate === date)
    .value()
    .length > 1;
};

/**
 * hasSplitShift checks whether a timecard or template's shifts contain at least one split shift
 * */
export const hasSplitShift = (shiftDates: ShiftDates): boolean => {
  const shiftDatesArr: string[] = _
    .chain(shiftDates)
    .map(({ date }) => date)
    .value();
  return shiftDatesArr.length !== _.uniq(shiftDatesArr).length;
};

export const _isPayPunchEmpty = (payPunches: UsaPunchValues): boolean => _
  .chain(payPunches)
  .omit('shiftId')
  .mapValues((shift) => shift)
  .filter(punchItem => !_.isUndefined(punchItem))
  .isEmpty()
  .value();

const _hasWrapValueOnly = (payPunches: UsaPunchValues): boolean => {
  const {wrap, ...otherPunches} = payPunches;
  return _isPayPunchEmpty(otherPunches) && !_.isNil(wrap);
};

// Helper function for comparing objects and finding differences
const _diff = (a: any, b: any): string[] => {
  return Object.keys(a).reduce((result, key) => {
    if (!b.hasOwnProperty(key)) {
      result.push(key);
    } else if (_.isEqual(_.get(a, key), _.get(b, key))) {
      const resultKeyIndex = result.indexOf(key);
      result.splice(resultKeyIndex, 1);
    }
    return result;
  }, Object.keys(b));
};

/**
 * Determines if UK breakage override calculation is required by comparing source timecard breakages
 * with saved API timecard breakages
 */
export const IsUkBreakageOverrideCalcRequired = (timecard: UkTimecard, apiTimecard: ApiUkTimecard|undefined): boolean => {
  if (_.isUndefined(apiTimecard)) {
    return false;
  }
  // Extract breakage data from source timecard
  const sourceBreakages: Partial<UkBreakages> = _.epDeepOmit(timecard.shifts.breakages, ['errorType', 'errorMessage']);
  // Extract breakage data from API timecard using exact clones of the mapper functions
  const savedBreakages: Partial<UkBreakages> = MapUkBreakages(apiTimecard.shifts || [], true);
  const shiftDiff = _diff(sourceBreakages, savedBreakages);
  return shiftDiff.length > 0;
};
